import { alpha, styled, type Theme } from '@mui/material/styles';
import { getZoomAdjustedPx } from 'components/ui/utilities/zoom';
import { getPrismScrollbarStyles } from 'theme';
import type {
  TabNavigationItemsViewportProps,
  TabNavigationTabProps,
  TabNavigationZoomProps,
} from './TabNavigation.props';

const getTabNavigationHoverBackgroundColor = (
  theme: Theme,
  isSelected: boolean,
  isDisabled?: boolean
) => {
  if (isSelected) {
    return theme.palette.neutral[300];
  }

  return isDisabled ? 'transparent' : alpha(theme.palette.neutral[300], 0.58);
};

const getTabNavigationHoverColor = (
  theme: Theme,
  isSelected: boolean,
  isDisabled?: boolean
) => {
  if (!isDisabled || isSelected) {
    return theme.palette.neutral[900];
  }

  return theme.palette.neutral[500];
};

const shouldForwardZoomProp = (prop: PropertyKey) => prop !== 'zoomLevel';

const getTabNavigationPx = (value: number, zoomLevel = 1) => getZoomAdjustedPx(value, zoomLevel);

export const TabNavigationRoot = styled('nav', {
  shouldForwardProp: shouldForwardZoomProp,
})<TabNavigationZoomProps>(({ theme, zoomLevel }) => ({
  alignItems: 'center',
  boxSizing: 'border-box',
  display: 'flex',
  gap: getTabNavigationPx(8, zoomLevel),
  height: getTabNavigationPx(32, zoomLevel),
  minWidth: 0,
  width: '100%',
  '--tab-navigation-scrollbar-width': getTabNavigationPx(6, zoomLevel),
  '--tab-navigation-scrollbar-thumb-border-width': getTabNavigationPx(2, zoomLevel),
  '--tab-navigation-scrollbar-thumb-border-color': theme.palette.neutral[100],
}));

export const TabNavigationItemsArea = styled('div', {
  shouldForwardProp: shouldForwardZoomProp,
})<TabNavigationZoomProps>(({ theme, zoomLevel }) => ({
  alignItems: 'center',
  display: 'flex',
  flex: '1 1 auto',
  gap: getTabNavigationPx(2, zoomLevel),
  minWidth: 0,
  '--tab-navigation-scrollbar-width': getTabNavigationPx(6, zoomLevel),
  '--tab-navigation-scrollbar-thumb-border-width': getTabNavigationPx(2, zoomLevel),
  '--tab-navigation-scrollbar-thumb-border-color': theme.palette.neutral[100],
}));

export const TabNavigationItemsViewport = styled('div', {
  shouldForwardProp: prop => prop !== 'showScrollButtons' && shouldForwardZoomProp(prop),
})<TabNavigationItemsViewportProps>(({ showScrollButtons = false, theme }) => ({
  flex: '1 1 auto',
  minWidth: 0,
  overflowX: 'auto',
  overflowY: 'hidden',
  ...getPrismScrollbarStyles(theme, {
    thumbBorderColor: 'var(--tab-navigation-scrollbar-thumb-border-color)',
    thumbBorderWidth: 'var(--tab-navigation-scrollbar-thumb-border-width)',
    width: 'var(--tab-navigation-scrollbar-width)',
  }),
  ...(showScrollButtons ? {
    msOverflowStyle: 'none',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  } : {}),
}));

export const TabNavigationItems = styled('div', {
  shouldForwardProp: shouldForwardZoomProp,
})<TabNavigationZoomProps>(({ zoomLevel }) => ({
  alignItems: 'center',
  display: 'flex',
  gap: getTabNavigationPx(4, zoomLevel),
  minWidth: 'max-content',
}));

export const TabNavigationTab = styled('button', {
  shouldForwardProp: prop => prop !== 'isSelected' && shouldForwardZoomProp(prop),
})<TabNavigationTabProps>(({ disabled, isSelected = false, theme, zoomLevel }) => ({
  alignItems: 'center',
  backgroundColor: isSelected ? theme.palette.neutral[300] : 'transparent',
  border: `${getTabNavigationPx(1, zoomLevel)} solid ${isSelected ? theme.palette.neutral[300] : 'transparent'}`,
  borderRadius: getTabNavigationPx(theme.shape.radius.xs, zoomLevel),
  boxSizing: 'border-box',
  color: isSelected ? theme.palette.neutral[900] : theme.palette.neutral[500],
  cursor: disabled ? 'not-allowed' : 'pointer',
  display: 'inline-flex',
  flex: '0 1 auto',
  fontFamily: theme.typography.fontFamily,
  fontSize: getTabNavigationPx(12, zoomLevel),
  fontWeight: isSelected ? 600 : theme.typography.fontWeightMedium,
  height: getTabNavigationPx(28, zoomLevel),
  lineHeight: getTabNavigationPx(16, zoomLevel),
  maxWidth: getTabNavigationPx(180, zoomLevel),
  minWidth: 0,
  opacity: disabled ? 0.48 : 1,
  outline: 0,
  padding: `${getTabNavigationPx(4, zoomLevel)} ${getTabNavigationPx(10, zoomLevel)}`,
  transition: theme.transitions.create(['background-color', 'border-color', 'box-shadow', 'color'], {
    duration: theme.transitions.duration.shortest,
    easing: theme.transitions.easing.easeOut,
  }),
  whiteSpace: 'nowrap',
  '&:hover': {
    backgroundColor: getTabNavigationHoverBackgroundColor(theme, isSelected, disabled),
    color: getTabNavigationHoverColor(theme, isSelected, disabled),
  },
  '&:focus-visible': {
    boxShadow: `0 0 0 ${getTabNavigationPx(2, zoomLevel)} ${alpha(theme.palette.brand[500], 0.22)}`,
  },
}));

export const TabNavigationTabLabel = styled('span')({
  flex: '1 1 auto',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const TabNavigationTabContent = styled('span', {
  shouldForwardProp: shouldForwardZoomProp,
})<TabNavigationZoomProps>(({ zoomLevel }) => ({
  alignItems: 'center',
  display: 'inline-flex',
  gap: getTabNavigationPx(6, zoomLevel),
  maxWidth: '100%',
  minWidth: 0,
}));

export const TabNavigationTabBadge = styled('span', {
  shouldForwardProp: shouldForwardZoomProp,
})<TabNavigationZoomProps>(({ theme, zoomLevel }) => ({
  alignItems: 'center',
  backgroundColor: 'transparent',
  color: theme.palette.brand[500],
  display: 'inline-flex',
  flex: '0 0 auto',
  fontSize: getTabNavigationPx(11, zoomLevel),
  fontWeight: 700,
  justifyContent: 'center',
  lineHeight: getTabNavigationPx(16, zoomLevel),
  minWidth: 0,
  padding: 0,
}));

export const TabNavigationActions = styled('div')({
  alignItems: 'center',
  display: 'flex',
  flex: '0 0 auto',
  minWidth: 0,
});

export const TabNavigationScrollButton = styled('button', {
  shouldForwardProp: shouldForwardZoomProp,
})<TabNavigationZoomProps>(({ theme, zoomLevel }) => ({
  alignItems: 'center',
  backgroundColor: 'transparent',
  border: 0,
  borderRadius: getTabNavigationPx(theme.shape.radius.xs, zoomLevel),
  color: theme.palette.neutral[500],
  cursor: 'pointer',
  display: 'inline-flex',
  flex: '0 0 auto',
  height: getTabNavigationPx(28, zoomLevel),
  justifyContent: 'center',
  margin: 0,
  padding: 0,
  transition: theme.transitions.create(['background-color', 'color'], {
    duration: theme.transitions.duration.shortest,
    easing: theme.transitions.easing.easeOut,
  }),
  width: getTabNavigationPx(24, zoomLevel),
  '& svg': {
    fontSize: getTabNavigationPx(16, zoomLevel),
  },
  '&:hover': {
    backgroundColor: alpha(theme.palette.neutral[300], 0.58),
    color: theme.palette.neutral[900],
  },
  '&:focus-visible': {
    outline: `${getTabNavigationPx(2, zoomLevel)} solid ${alpha(theme.palette.brand[500], 0.38)}`,
    outlineOffset: `-${getTabNavigationPx(2, zoomLevel)}`,
  },
  '&:disabled': {
    color: theme.palette.neutral[400],
    cursor: 'default',
  },
  '&:disabled:hover': {
    backgroundColor: 'transparent',
    color: theme.palette.neutral[400],
  },
}));
